import { ReactNode, useMemo } from 'react';
import { Link, useLocation } from 'wouter';
import { motion } from 'framer-motion';
import {
  LayoutDashboard,
  Calendar,
  CreditCard,
  MessageSquare,
  MapPin,
  Users,
  BarChart3,
  LineChart,
  Ticket,
  ArrowLeft,
  PieChart,
  Link2,
  LayoutGrid,
  ClipboardCheck,
  Rss,
  PartyPopper,
  Bot,
  Inbox,
  BookOpen,
  LucideIcon
} from 'lucide-react';
import { useProfile } from '@/lib/api';
import { useKikiFlagCount } from '../api/useAdminKikis';
import { useOpsPendingDecisionCount } from '../api/useAdminOps';
import { useTriageCounts } from '../api/useAdminTriage';
import { useClubReadyCount } from '../api/useAdminClubs';

interface AdminLayoutProps {
  children: ReactNode;
}

interface TabConfig {
  path: string;
  label: string;
  icon: LucideIcon;
  requiresAdmin?: boolean; // If true, only visible to admins. If false/undefined, visible to analytics users too.
}

const allTabs: TabConfig[] = [
  { path: '/', label: 'Dashboard', icon: LayoutDashboard, requiresAdmin: true },
  { path: '/ops', label: 'Event Ops', icon: Bot, requiresAdmin: true },
  { path: '/triage', label: 'Triage', icon: Inbox, requiresAdmin: true },
  { path: '/events', label: 'Events', icon: Calendar, requiresAdmin: true },
  { path: '/payments', label: 'Payments', icon: CreditCard, requiresAdmin: true },
  { path: '/feedback', label: 'Feedback', icon: MessageSquare, requiresAdmin: true },
  { path: '/chats', label: 'Chats', icon: MessageSquare, requiresAdmin: true },
  { path: '/users', label: 'Users', icon: Users, requiresAdmin: true },
  { path: '/analytics', label: 'Analytics', icon: LineChart, requiresAdmin: false },
  { path: '/demographics', label: 'Demographics', icon: PieChart, requiresAdmin: false },
  { path: '/bookings', label: 'Bookings', icon: Ticket, requiresAdmin: false },
  { path: '/connections', label: 'Connections', icon: Link2, requiresAdmin: true },
  { path: '/neighborhoods', label: 'Neighborhoods', icon: MapPin, requiresAdmin: true },
  { path: '/polls', label: 'Polls', icon: BarChart3, requiresAdmin: true },
  { path: '/clubs', label: 'Clubs', icon: BookOpen, requiresAdmin: true },
  { path: '/landing-variants', label: 'Landing Pages', icon: LayoutGrid, requiresAdmin: true },
  { path: '/host-events', label: 'Event Review', icon: ClipboardCheck, requiresAdmin: true },
  { path: '/kiki-review', label: 'Kiki Review', icon: PartyPopper, requiresAdmin: true },
  { path: '/social-crawling', label: 'Social Crawl', icon: Rss, requiresAdmin: true },
];

export default function AdminLayout({ children }: AdminLayoutProps) {
  const [location] = useLocation();
  const { data: profile } = useProfile({ enabled: true });
  
  const isAdmin = profile?.data?.isAdmin;
  // hasAnalyticsAccess includes analytics role OR admin role
  const hasAnalyticsAccess = (profile?.data as { hasAnalyticsAccess?: boolean })?.hasAnalyticsAccess || isAdmin;

  // Count of kikis flagged by the content scanner awaiting admin review —
  // shown as a notification badge on the Kiki Review tab.
  const { data: kikiFlags } = useKikiFlagCount({ enabled: !!isAdmin });
  const kikiFlagCount = kikiFlags?.count ?? 0;

  // Pending event-ops decisions awaiting a human — badge on the Event Ops tab.
  const { data: opsPending } = useOpsPendingDecisionCount({ enabled: !!isAdmin });
  const opsPendingCount = opsPending ?? 0;

  // Open triage tickets plus pending playbook proposals — badge on the
  // Triage tab (same wiring as the ops decision badge).
  const { data: triageCounts } = useTriageCounts({ enabled: !!isAdmin });
  const triagePendingCount = (triageCounts?.open ?? 0) + (triageCounts?.pendingPlaybookProposals ?? 0);

  // Club cities that reached their member threshold without an event yet.
  const { data: clubReadyCount = 0 } = useClubReadyCount({ enabled: !!isAdmin });
  
  // Filter tabs based on user permissions
  const visibleTabs = useMemo(() => {
    if (isAdmin) {
      // Admins see all tabs
      return allTabs;
    }
    if (hasAnalyticsAccess) {
      // Analytics users only see non-admin-required tabs
      return allTabs.filter(tab => !tab.requiresAdmin);
    }
    return [];
  }, [isAdmin, hasAnalyticsAccess]);

  return (
    <div className="min-h-screen">
      {/* Tab Navigation */}
      <div className="border-b border-divider fixed z-10 bg-white dark:bg-neutral-900 w-full top-safe-padding">
        <div className="max-w-7xl mx-auto px-4">
          <nav className="flex items-center space-x-1 overflow-x-auto">
            {/* Back to main app */}
            <a 
              href="https://app.outclose.com"
              className="flex items-center justify-center p-3 mr-2 text-dark-muted hover:text-primary-500 transition-colors"
              title="Back to Outclose"
            >
              <ArrowLeft className="w-5 h-5" />
            </a>
            {visibleTabs.map((tab) => {
              const isActive = location === tab.path;
              const Icon = tab.icon;
              const badgeCount =
                tab.path === '/kiki-review'
                  ? kikiFlagCount
                  : tab.path === '/ops'
                    ? opsPendingCount
                    : tab.path === '/triage'
                      ? triagePendingCount
                      : tab.path === '/clubs'
                        ? clubReadyCount
                        : 0;

              return (
                <Link
                  key={tab.path}
                  href={tab.path}
                  className={`relative flex items-center space-x-2 px-4 py-4 font-medium text-sm transition-colors whitespace-nowrap ${
                    isActive
                      ? 'text-primary-500'
                      : 'text-dark-muted hover:text-primary-800'
                  }`}
                >
                  <span className="relative">
                    <Icon className="w-4 h-4" />
                    {badgeCount > 0 && (
                      <span className="absolute -top-1.5 -right-1.5 w-1.5 h-1.5 rounded-full bg-red-500" />
                    )}
                  </span>
                  <span>{tab.label}</span>
                  {badgeCount > 0 && (
                    <span className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none">
                      {badgeCount > 99 ? '99+' : badgeCount}
                    </span>
                  )}
                  {isActive && (
                    <motion.div
                      layoutId="activeTab"
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-500"
                      initial={false}
                      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                    />
                  )}
                </Link>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Page Content */}
      <div className="top-safe-padding">
        <div className="pt-20 pb-8 px-4">
          {children}
        </div>
      </div>
    </div>
  );
}

